"use client";

import {
  AudioWaveformIcon,
  Check,
  ChevronsUpDown,
  GalleryVerticalEnd,
  Settings,
  UserPlus,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/shared/components/ui/sidebar";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "@/core/redux/store";
import { Skeleton } from "./skeleton";
import { useEffect, useState } from "react";
import { getUserWorkspaces } from "@/core/redux/thunks/workspaceThunk";
import { IWorkspace } from "@/shared/services/interface/responses/IWorkspaceResponses";
import { getDashboardLink } from "@/shared/utils/link";
import { ROUTES } from "@/core/router/routes";
import { useNavigate } from "react-router-dom";

export function NavWorkspace({
  absoluteProfile = false,
  skeleton = false,
}: {
  absoluteProfile?: boolean;
  skeleton?: boolean;
}) {
  const me = useSelector((state) => state.user.me);
  const workspaces = useSelector((state) => state.workspace.workspaces);
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const [activeWorkspace, setActiveWorkspace] = useState<IWorkspace | null>(
    workspaces?.find((workspace) => workspace.isCurrentWorkspace) || null
  );
  const navigate = useNavigate();
  const sidebarIsOpen = useSelector((state) => state.sidebar.isOpen);

  const getWorkspaces = async () => {
    const response = await dispatch(getUserWorkspaces()).unwrap();
    if (response.success) {
      setActiveWorkspace(
        response.data.workspaces?.find(
          (workspace) => workspace.isCurrentWorkspace
        ) || null
      );
    }
  };
  useEffect(() => {
    getWorkspaces();
  }, []);

  return (
    <SidebarMenu
      className={` w-full ${
        absoluteProfile
          ? sidebarIsOpen
            ? "absolute left-0 bottom-[8px]"
            : "absolute left-2 bottom-[8px]"
          : ""
      }`}
    >
      <SidebarMenuItem>
        {!skeleton && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild className="">
              <SidebarMenuButton
                size="lg"
                className={`data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground`}
              >
                <div className="flex flex-row items-center justify-center gap-2 h-7 w-7 rounded-md bg-zinc-950 p-1">
                  <GalleryVerticalEnd className="w-4 h-4 text-white" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-700 text-zinc-950">
                    {activeWorkspace?.name || "loading..."}
                  </span>
                  <span className="truncate text-xs text-muted-foreground font-normal">
                    1 Member
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side={"bottom"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuGroup className="cursor-pointer">
                <DropdownMenuItem className="cursor-pointer">
                  <span className="text-zinc-500 text-[12px] font-500">
                    {me?.email}
                  </span>
                </DropdownMenuItem>
              </DropdownMenuGroup>

              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => {
                    navigate(
                      getDashboardLink(ROUTES.Dashboard.Setting.MyProfile)
                    );
                  }}
                >
                  <Settings />
                  <span className="text-zinc-950 text-[14px] font-500 mt-0.5">
                    {t("settings")}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => {
                    navigate(
                      getDashboardLink(ROUTES.Dashboard.Setting.Members)
                    );
                  }}
                >
                  <UserPlus className="w-4 h-4" />
                  <span className="text-zinc-950 text-[14px] font-500 mt-0.5">
                    {t("invite-and-manage")}
                  </span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />

              <DropdownMenuGroup className="max-h-[150px] overflow-y-auto">
                <div className="px-2 py-1.5">
                  <span className="text-muted-foreground text-[12px] font-500 mt-0.5">
                    {t("workspace")}
                  </span>
                </div>
                {workspaces?.map((workspace) => (
                  <DropdownMenuItem
                    key={workspace.id}
                    className="cursor-pointer w-full flex flex-row items-center justify-between"
                    onClick={() =>
                      navigate(
                        getDashboardLink(
                          ROUTES.Dashboard.Setting.Workspace.SwitchLoading
                        ) + `?workspaceId=${workspace?.id}&returnUrl=/`
                      )
                    }
                  >
                    <div className="flex flex-row items-center justify-center gap-2">
                      <div
                        className={`w-6 h-6 flex items-center justify-center rounded-[4px] ${
                          workspace?.isCurrentWorkspace
                            ? "bg-zinc-950"
                            : "bg-[#E9E9E9]"
                        }`}
                      >
                        <AudioWaveformIcon
                          className={`w-4 h-4 ${
                            workspace?.isCurrentWorkspace
                              ? "text-white"
                              : "text-zinc-950"
                          }`}
                        />
                      </div>
                      <span className="text-zinc-950 text-[14px] font-500 mt-0.5 w-[80px] truncate">
                        {workspace.name}
                      </span>
                    </div>
                    {workspace?.isCurrentWorkspace && (
                      <Check className="w-4 h-4 text-zinc-950" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {skeleton && (
          <div className="flex items-center justify-between gap-2    ">
            <Skeleton className="h-8 w-8 bg-zinc-100 rounded-[6px]" />
            <div className="flex flex-col gap-1 w-full">
              <Skeleton className="w-4/6 h-5 bg-zinc-100 rounded-[6px]" />
              <Skeleton className="w-4/6 h-5 bg-zinc-100 rounded-[6px]" />
            </div>
            <Skeleton className="h-5 w-5 bg-zinc-100 rounded-[6px]" />
          </div>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
